/**
 * Analytics Service
 * 
 * A centralized service for tracking user interactions and events throughout the application.
 * This implementation provides a facade that can work with different analytics providers
 * like Google Analytics, Mixpanel, or your own custom backend.
 */

// Determine if we're in development or production
const isDevelopment = process.env.NODE_ENV === 'development';

// Configuration
const config = {
  // Disable analytics globally until backend is ready
  enabled: false, // Set to false to disable all analytics
  debug: isDevelopment,
  providers: {
    googleAnalytics: false,
    mixpanel: false,
    customBackend: false // Also disable custom backend
  }
};

/**
 * Log analytics events to the console in development
 */
const logToConsole = (eventName, eventData) => {
  if (config.debug) {
    console.log(`[Analytics] ${eventName}:`, eventData);
  }
};

/**
 * Send event to Google Analytics
 * This is a placeholder - replace with actual GA implementation
 */
const sendToGA = (eventName, eventData) => {
  if (!config.providers.googleAnalytics) return;
  
  // Example GA4 implementation (replace with actual code)
  if (window.gtag) {
    window.gtag('event', eventName, eventData);
  }
};

/**
 * Send event to Mixpanel
 * This is a placeholder - replace with actual Mixpanel implementation
 */
const sendToMixpanel = (eventName, eventData) => {
  if (!config.providers.mixpanel) return;
  
  // Example Mixpanel implementation (replace with actual code)
  if (window.mixpanel) {
    window.mixpanel.track(eventName, eventData);
  }
};

/**
 * Send event to custom backend API
 */
const sendToCustomBackend = async (eventName, eventData) => {
  if (!config.providers.customBackend) return;
  
  try {
    const payload = {
      event: eventName,
      data: eventData,
      timestamp: new Date().toISOString(),
      sessionId: getSessionId(),
      userId: getUserId()
    };
    
    // Don't actually send API requests in development mode
    if (isDevelopment) {
      logToConsole('API Request', payload);
      return;
    }
    
    // Send to backend API
    const response = await fetch('/api/analytics/events', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    
    if (!response.ok) {
      throw new Error('Failed to send analytics event to backend');
    }
  } catch (error) {
    console.error('Analytics error:', error);
  }
};

/**
 * Get or create a unique session ID
 */
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('analytics_session_id');
  if (!sessionId) {
    sessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    sessionStorage.setItem('analytics_session_id', sessionId);
  }
  return sessionId;
};

/**
 * Get user ID if available
 */
const getUserId = () => {
  // This could be retrieved from your auth system
  return localStorage.getItem('user_id') || null;
};

/**
 * Track a page view
 */
export const trackPageView = (page = null) => {
  if (!config.enabled) return;
  
  // Use current path if page not specified
  const currentPage = page || window.location.pathname;
  
  const eventData = {
    page: currentPage,
    title: document.title,
    referrer: document.referrer
  };
  
  logToConsole('Page View', eventData);
  sendToGA('page_view', eventData);
  sendToMixpanel('page_view', eventData);
  sendToCustomBackend('page_view', eventData);
};

/**
 * Track a feature usage
 */
export const trackFeatureUsage = (featureName, action, additionalData = {}) => {
  if (!config.enabled) return;
  
  const eventData = {
    feature: featureName,
    action: action,
    ...additionalData
  };
  
  logToConsole('Feature Usage', eventData);
  sendToGA('feature_usage', eventData);
  sendToMixpanel('feature_usage', eventData);
  sendToCustomBackend('feature_usage', eventData);
};

/**
 * Track prayer-specific events
 */
export const trackPrayerEvent = (prayerName, eventType, additionalData = {}) => {
  if (!config.enabled) return;
  
  const eventData = {
    prayer: prayerName,
    eventType: eventType,
    ...additionalData
  };
  
  logToConsole('Prayer Event', eventData);
  sendToGA('prayer_event', eventData);
  sendToMixpanel('prayer_event', eventData);
  sendToCustomBackend('prayer_event', eventData);
};

/**
 * Track a search action
 */
export const trackSearch = (query, category, results = null) => {
  if (!config.enabled) return;
  
  const eventData = {
    query: query,
    category: category,
    resultsCount: results ? results.length : null
  };
  
  logToConsole('Search', eventData);
  sendToGA('search', eventData);
  sendToMixpanel('search', eventData);
  sendToCustomBackend('search', eventData);
};

/**
 * Track errors that occur
 */
export const trackError = (errorType, errorMessage, additionalData = {}) => {
  if (!config.enabled) return;
  
  const eventData = {
    type: errorType,
    message: errorMessage,
    ...additionalData
  };
  
  logToConsole('Error', eventData);
  sendToGA('error', eventData);
  sendToMixpanel('error', eventData);
  sendToCustomBackend('error', eventData);
};

/**
 * Track user interaction with Athan features
 */
export const trackAthanEvent = (action, athanType, additionalData = {}) => {
  if (!config.enabled) return;
  
  const eventData = {
    action: action, // 'play', 'stop', 'settings_changed', etc.
    athanType: athanType, // 'default', 'fajr', etc.
    ...additionalData
  };
  
  logToConsole('Athan Event', eventData);
  sendToGA('athan_event', eventData);
  sendToMixpanel('athan_event', eventData);
  sendToCustomBackend('athan_event', eventData);
};

/**
 * Track Prayer Intent Assistant usage
 */
export const trackPrayerIntentEvent = (action, query = null, responseType = null, additionalData = {}) => {
  if (!config.enabled) return;
  
  const eventData = {
    action: action, // 'query', 'save_intention', 'view_history', etc.
    query: query, // The user's input (can be null for some actions)
    responseType: responseType, // What kind of response was given
    ...additionalData
  };
  
  logToConsole('Prayer Intent Event', eventData);
  sendToGA('prayer_intent', eventData);
  sendToMixpanel('prayer_intent', eventData);
  sendToCustomBackend('prayer_intent', eventData);
};

/**
 * Initialize analytics - call this once at app startup
 */
export const initAnalytics = (options = {}) => {
  // Merge provided options with default config
  Object.assign(config, options);
  
  // Track initial page view
  trackPageView();
  
  // Add additional initialization here if needed
  logToConsole('Analytics Initialized', { providers: config.providers });
};

// Export all functions
const analyticsService = {
  initAnalytics,
  trackPageView,
  trackFeatureUsage,
  trackPrayerEvent,
  trackSearch,
  trackError,
  trackAthanEvent,
  trackPrayerIntentEvent
};

export default analyticsService;